import path from "path";
import fs from "fs";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import type { AuditResult, AuditFinding } from "../models/AuditResult.js";

const LOCKFILES = [
  { name: "package-lock.json", pm: "npm" },
  { name: "yarn.lock", pm: "yarn" },
  { name: "pnpm-lock.yaml", pm: "pnpm" },
  { name: "bun.lock", pm: "bun" },
];

/**
 * Runs all drift detection checks against the current project.
 *
 * Checks (all offline, no network):
 *  1. Lockfile presence (no lockfile = drift risk)
 *  2. Multiple lockfiles (conflicting package managers)
 *  3. node_modules presence and basic consistency
 *  4. iOS Podfile.lock vs Podfile drift
 *  5. Android local.properties existence
 *  6. Gradle wrapper files presence
 *  7. Plugin drift (loaded plugins vs expected from deps)
 */
export async function runAudit(cwd: string): Promise<AuditResult> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) {
    throw new Error("package.json not found. Run from within a React Native project.");
  }

  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) throw new Error("package.json is corrupted.");

  const projectRoot = path.dirname(packageJsonPath);
  const findings: AuditFinding[] = [];

  // ── 1. Lockfile checks ─────────────────────────────────────────────────────
  const presentLockfiles = LOCKFILES.filter((lf) =>
    fs.existsSync(path.join(projectRoot, lf.name))
  );

  if (presentLockfiles.length === 0) {
    findings.push({
      severity: "warning",
      category: "lockfile",
      message: "No lockfile found (package-lock.json, yarn.lock, pnpm-lock.yaml, bun.lock).",
      suggestion: "Commit a lockfile to ensure reproducible builds.",
    });
  } else if (presentLockfiles.length > 1) {
    findings.push({
      severity: "warning",
      category: "lockfile",
      message: `Multiple lockfiles detected: ${presentLockfiles.map((l) => l.name).join(", ")}.`,
      detail: "Multiple lockfiles suggest conflicting package managers.",
      suggestion: "Remove all but the lockfile for your primary package manager.",
    });
  } else {
    findings.push({
      severity: "info",
      category: "lockfile",
      message: `Lockfile present: ${presentLockfiles[0].name} (${presentLockfiles[0].pm})`,
    });
  }

  // ── 2. node_modules check ─────────────────────────────────────────────────
  const nodeModulesPath = path.join(projectRoot, "node_modules");
  if (!fs.existsSync(nodeModulesPath)) {
    findings.push({
      severity: "error",
      category: "dependencies",
      message: "node_modules directory not found — dependencies are not installed.",
      suggestion: "Run your package manager install command (npm install / yarn / pnpm install).",
    });
  } else {
    // Check if top-level package count in node_modules seems reasonable
    const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
    const topLevelModules = Object.keys(dependencies).filter(
      (dep) => !dep.startsWith("@") || dep.split("/").length <= 2
    );

    let missingCount = 0;
    for (const dep of topLevelModules.slice(0, 20)) {
      // Sample check
      if (!fs.existsSync(path.join(nodeModulesPath, dep))) {
        missingCount++;
      }
    }
    if (missingCount > 0) {
      findings.push({
        severity: "warning",
        category: "dependencies",
        message: `${missingCount} sampled package(s) missing from node_modules.`,
        suggestion: "Run your package manager install command to restore dependencies.",
      });
    } else {
      findings.push({
        severity: "info",
        category: "dependencies",
        message: "node_modules appears consistent with package.json.",
      });
    }
  }

  // ── 3. iOS: Podfile / Podfile.lock drift ───────────────────────────────────
  const iosDir = path.join(projectRoot, "ios");
  if (fs.existsSync(iosDir)) {
    const podfilePath = path.join(iosDir, "Podfile");
    const podfileLockPath = path.join(iosDir, "Podfile.lock");

    if (!fs.existsSync(podfilePath)) {
      findings.push({
        severity: "warning",
        category: "ios-pods",
        message: "ios/ directory exists but Podfile is missing.",
        suggestion: "Run: kyro repair or kyro sync to restore Podfile.",
      });
    } else if (!fs.existsSync(podfileLockPath)) {
      findings.push({
        severity: "warning",
        category: "ios-pods",
        message: "Podfile exists but Podfile.lock is missing — pods may not be installed.",
        suggestion: "Run: pod install --project-directory=ios",
      });
    } else {
      // Check if Podfile.lock is stale vs Podfile modification time
      try {
        const podfileStat = fs.statSync(podfilePath);
        const podfileLockStat = fs.statSync(podfileLockPath);
        if (podfileStat.mtimeMs > podfileLockStat.mtimeMs) {
          findings.push({
            severity: "warning",
            category: "ios-pods",
            message: "Podfile has been modified since Podfile.lock was last generated.",
            suggestion: "Run: pod install --project-directory=ios",
          });
        } else {
          findings.push({
            severity: "info",
            category: "ios-pods",
            message: "Podfile.lock is in sync with Podfile.",
          });
        }
      } catch {
        // ignore stat errors
      }
    }
  } else {
    findings.push({
      severity: "info",
      category: "ios-pods",
      message: "No ios/ directory found — iOS platform not set up.",
    });
  }

  // ── 4. Android: Gradle wrapper / local.properties ──────────────────────────
  const androidDir = path.join(projectRoot, "android");
  if (fs.existsSync(androidDir)) {
    const gradlewPath = path.join(androidDir, "gradlew");
    const gradleWrapperProps = path.join(androidDir, "gradle", "wrapper", "gradle-wrapper.properties");
    const localPropsPath = path.join(androidDir, "local.properties");

    if (!fs.existsSync(gradlewPath)) {
      findings.push({
        severity: "error",
        category: "android-gradle",
        message: "android/gradlew not found — Gradle wrapper is missing.",
        suggestion: "Run: kyro repair to restore Gradle wrapper.",
      });
    } else if (!fs.existsSync(gradleWrapperProps)) {
      findings.push({
        severity: "warning",
        category: "android-gradle",
        message: "gradle-wrapper.properties not found.",
        suggestion: "Restore from React Native template or run: kyro repair",
      });
    } else {
      findings.push({
        severity: "info",
        category: "android-gradle",
        message: "Gradle wrapper files are present.",
      });
    }

    if (!fs.existsSync(localPropsPath)) {
      findings.push({
        severity: "warning",
        category: "android-gradle",
        message: "android/local.properties not found.",
        detail: "This file specifies the Android SDK path required by Gradle.",
        suggestion: "Create local.properties with: sdk.dir=/path/to/your/android/sdk",
      });
    }
  } else {
    findings.push({
      severity: "info",
      category: "android-gradle",
      message: "No android/ directory found — Android platform not set up.",
    });
  }

  // ── 5. Plugin drift ────────────────────────────────────────────────────────
  try {
    const loadedPlugins = await loadPluginsForProject(projectRoot);
    if (loadedPlugins.length === 0) {
      findings.push({
        severity: "info",
        category: "plugins",
        message: "No plugins loaded for this project.",
      });
    } else {
      findings.push({
        severity: "info",
        category: "plugins",
        message: `${loadedPlugins.length} plugin(s) active: ${loadedPlugins.map((p) => p.packageName).join(", ")}`,
      });

      // Run sync validations from each plugin and report
      for (const plugin of loadedPlugins) {
        if (plugin.syncValidations) {
          const validations = await plugin.syncValidations(projectRoot);
          for (const v of validations) {
            findings.push({
              severity: "warning",
              category: "plugin-drift",
              message: `[${plugin.packageName}] ${v}`,
              suggestion: "Review plugin setup documentation.",
            });
          }
        }
      }
    }
  } catch {
    findings.push({
      severity: "info",
      category: "plugins",
      message: "Plugin loading skipped (may not be a React Native project).",
    });
  }

  // ── Partition and build result ─────────────────────────────────────────────
  const errors = findings.filter((f) => f.severity === "error");
  const warnings = findings.filter((f) => f.severity === "warning");
  const infos = findings.filter((f) => f.severity === "info");

  return {
    errors,
    warnings,
    infos,
    totalFindings: findings.length,
    passed: errors.length === 0 && warnings.length === 0,
    generatedAt: new Date().toISOString(),
  };
}
